import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import apiService from "../services/apiService";
import toast from "react-hot-toast";
import { mergeAnalyticsPayloads } from "../utils/mergeAnalytics";

const FIGMA_BLUE = "#00A3FF";
const FIGMA_SALMON = "#FF6B6B";

function formatInt(n) {
  if (n == null || Number.isNaN(n)) return "0";
  return Number(n).toLocaleString();
}

/** Compact human-readable counts: 999, 1k, 5k, 1.2m, 3b */
function formatCompactInt(n) {
  if (n == null || Number.isNaN(Number(n))) return "0";
  let num = Number(n);
  const sign = num < 0 ? "-" : "";
  num = Math.abs(num);
  if (num < 1000) return sign + String(Math.round(num));

  const tiers = [
    { d: 1e9, s: "B" },
    { d: 1e6, s: "M" },
    { d: 1e3, s: "K" },
  ];
  for (const { d, s } of tiers) {
    if (num >= d) {
      const x = num / d;
      const decimals = x >= 100 ? 0 : x >= 10 ? 1 : 2;
      const out = String(Number(x.toFixed(decimals)));
      return sign + out + s;
    }
  }
  return sign + String(Math.round(num));
}

/** Accepts a 0..1 ratio and renders it as a 0..100% string. Both call
 *  sites here pass `used / limit`, never a pre-multiplied percent. */
function formatPercent(ratio) {
  if (!Number.isFinite(ratio)) return "0%";
  const pct = ratio * 100;
  const rounded = Math.round(pct * 10) / 10;
  return `${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1)}%`;
}

function AnimatedBar({ ratio, color, delay = 0 }) {
  const rawPct = Math.min(100, Math.max(0, (ratio || 0) * 100));
  const pct = rawPct > 0 && rawPct < 2 ? 2 : rawPct;
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-white/[0.08]">
      <motion.div
        className="h-full w-full origin-left rounded-full"
        style={{ backgroundColor: color }}
        initial={{ scaleX: 0 }}
        animate={{ scaleX: pct / 100 }}
        transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay }}
      />
    </div>
  );
}

/**
 * Figma-style usage panel: total / daily / monthly with animated bars.
 * Aggregates analytics across all API keys in the project.
 */
export default function DashboardUsageStatistics({ apiKeys }) {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const containerRef = useRef(null);

  const load = useCallback(async () => {
    if (!apiKeys?.length) {
      setAnalytics(null);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      // Use allSettled so a single failed key (e.g. just deleted →
      // 404 from the analytics endpoint while the parent's keys list
      // is still being refreshed) doesn't fail the whole batch and
      // surface a misleading "Failed to load usage statistics" toast.
      // Filter to fulfilled values, then merge.
      const settled = await Promise.allSettled(
        apiKeys.map((k) => apiService.getApiKeyAnalytics(k.uuid, "30d")),
      );
      const ok = settled
        .filter((r) => r.status === "fulfilled" && r.value)
        .map((r) => r.value);
      if (ok.length > 0) {
        setAnalytics(mergeAnalyticsPayloads(ok));
      } else {
        // All failed — only then show the error.
        toast.error("Failed to load usage statistics");
        setAnalytics(null);
      }
    } catch {
      toast.error("Failed to load usage statistics");
      setAnalytics(null);
    } finally {
      setLoading(false);
    }
  }, [apiKeys]);

  useEffect(() => {
    load();
  }, [load]);

  if (!apiKeys?.length) return null;

  if (loading) {
    return (
      <div
        ref={containerRef}
        className="flex min-h-[220px] items-center justify-center rounded-2xl border border-white/[0.08] bg-[#141414] p-6"
      >
        <div className="h-9 w-9 animate-spin rounded-full border-2 border-[#00A3FF] border-t-transparent" />
      </div>
    );
  }

  if (!analytics?.limits) {
    return (
      <div className="rounded-2xl border border-white/[0.08] bg-[#141414] p-6 font-secondary text-sm text-white/50">
        No usage data yet
      </div>
    );
  }

  const L = analytics.limits;
  const monthlyLimit = L.monthly_limit;
  const dailyLimit = L.daily_limit;
  const archiveLimit = L.archive_limit;
  const monthlyUsed = Number(L.monthly_used) || 0;
  const dailyUsed = Number(L.daily_used) || 0;
  const archiveUsed = Number(L.archive_used) || 0;
  // Raw full-bucket count (NOT CU-weighted). Distinct from monthly_used,
  // which is the weighted total that fills the plan's monthly cap.
  const fullUsed = Number(L.full_used) || 0;

  const dailyRatio =
    dailyLimit && dailyLimit > 0 ? Math.min(1, dailyUsed / dailyLimit) : 0;
  const monthlyRatio =
    monthlyLimit && monthlyLimit > 0
      ? Math.min(1, monthlyUsed / monthlyLimit)
      : 0;
  const archiveRatio =
    archiveLimit && archiveLimit > 0
      ? Math.min(1, archiveUsed / archiveLimit)
      : 0;

  const dailyPct =
    dailyLimit && dailyLimit > 0 ? dailyUsed / dailyLimit : 0;
  const monthlyPct =
    monthlyLimit && monthlyLimit > 0 ? monthlyUsed / monthlyLimit : 0;
  // Overage state — see UsageDashboard.jsx for the full rationale.
  const monthlyOver = !!(monthlyLimit && monthlyUsed > monthlyLimit);
  const overAmount = monthlyOver ? monthlyUsed - monthlyLimit : 0;
  const overageBilling = monthlyOver && !!L.overage_enabled;
  const usdPerMillionFull = L.usd_per_million_full;
  const archivePct =
    archiveLimit && archiveLimit > 0 ? archiveUsed / archiveLimit : 0;

  return (
    <motion.div
      ref={containerRef}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: "easeOut" }}
      className="relative flex h-full flex-col rounded-sm bg-[#1a1a1a] p-5 sm:p-6"
    >
      <div
        className="pointer-events-none absolute inset-0 z-0"
        style={{
          background:
            "linear-gradient(0deg, #0E0E0E 0%, rgba(14, 14, 14, 0) 50%, rgba(14, 14, 14, 0) 100%)",
        }}
      />
      <div className="relative z-10">
        <p className="font-title mb-5 text-xs uppercase font-semibold tracking-[0.2em] text-white/75">
          Usage statistics
        </p>

        <div className="space-y-6">
          {/* Total requests moved out to its own card in DashboardInsights —
              it's the headline number and deserves the larger surface.
              Right rail keeps the quota-oriented metrics below. */}

          <div>
            <p className="font-secondary mb-2 text-sm text-white/70">
              Daily requests
            </p>
            <div className="mb-2 flex items-baseline justify-between gap-2">
              <p className="font-title text-lg font-semibold tracking-tight text-white sm:text-xl">
                {formatCompactInt(dailyUsed)}{" "}
                <span className="text-white/40 text-sm font-secondary font-medium">
                  requests
                </span>
              </p>
              {dailyLimit != null ? (
                <span
                  className="font-title text-sm font-semibold"
                  style={{ color: FIGMA_SALMON }}
                >
                  {formatInt(L.daily_remaining ?? 0)} left
                </span>
              ) : null}
              {/* No "Unlimited" label when there's no daily cap. Daily count
                  is incremented even on cap-less plans (Free etc), so the
                  label was misleading — daily is tracked, just no hard cap. */}
            </div>
            {dailyLimit ? (
              <AnimatedBar ratio={dailyRatio} color={FIGMA_SALMON} delay={0.15} />
            ) : null}
          </div>

          {/* Monthly usage — what the gateway charges against the plan's
              monthly cap. CU-WEIGHTED: archive requests count into this
              counter at the plan's archive_weight multiplier (Free = 6×),
              so 1 archive call shows up as 6 units here. The plain
              "Full node requests" line below shows the raw count of
              full-bucket calls for the same period, distinct from this
              weighted total. */}
          <div>
            <p className="font-secondary mb-2 text-sm text-white/70 flex items-center gap-2">
              Monthly usage
              <span className="text-xs text-white/40 font-normal normal-case">
                (plan quota)
              </span>
              {overageBilling ? (
                <span className="rounded-sm border border-amber-400/40 bg-amber-400/10 px-1.5 py-0.5 font-title text-[10px] font-semibold uppercase tracking-wider text-amber-300">
                  Overage
                </span>
              ) : monthlyOver ? (
                <span className="rounded-sm border border-red-400/40 bg-red-400/10 px-1.5 py-0.5 font-title text-[10px] font-semibold uppercase tracking-wider text-red-300">
                  Cap exceeded
                </span>
              ) : null}
            </p>
            <div className="mb-2 flex items-baseline justify-between gap-2">
              <p className="font-title text-lg font-semibold tracking-tight text-white sm:text-xl">
                {formatCompactInt(monthlyUsed)}
                {monthlyLimit ? (
                  <span className="text-white/40 text-sm font-secondary font-medium">
                    {" "}/ {formatCompactInt(monthlyLimit)}
                  </span>
                ) : (
                  <span className="text-white/40 text-sm font-secondary font-medium">
                    {" "}requests
                  </span>
                )}
              </p>
              <span
                className={`font-title text-sm font-semibold ${
                  monthlyOver
                    ? overageBilling
                      ? "text-amber-300"
                      : "text-red-300"
                    : "text-primary"
                }`}
              >
                {formatPercent(monthlyPct)}
              </span>
            </div>
            {monthlyLimit ? (
              <AnimatedBar ratio={monthlyRatio} color={FIGMA_BLUE} delay={0.25} />
            ) : (
              <div className="h-2 w-full rounded-full bg-white/[0.08]" />
            )}
            {overageBilling ? (
              <p className="font-secondary mt-2 text-xs text-amber-300/90">
                {formatCompactInt(overAmount)} over limit
                {usdPerMillionFull != null ? (
                  <> · ${usdPerMillionFull}/M billed</>
                ) : null}
              </p>
            ) : monthlyOver ? (
              <p className="font-secondary mt-2 text-xs text-red-300/90">
                Cap exceeded — requests blocked
              </p>
            ) : null}
          </div>

          {/* Full node requests — RAW count of full-bucket calls this
              month, sourced from live Redis usage:bucket:full counters.
              Not weighted, no cap of its own (the plan's monthly quota
              above is the cap). Shown so customers can reason about
              full vs archive composition independently from the
              CU-weighted Monthly usage above. */}
          <div>
            <p className="font-secondary mb-2 text-sm text-white/70">
              Full node requests
              <span className="ml-2 text-xs text-white/40 font-normal normal-case">
                (this month, raw count)
              </span>
            </p>
            <div className="mb-2 flex items-baseline justify-between gap-2">
              <p className="font-title text-lg font-semibold tracking-tight text-white sm:text-xl">
                {formatCompactInt(fullUsed)}
                <span className="text-white/40 text-sm font-secondary font-medium">
                  {" "}requests
                </span>
              </p>
            </div>
          </div>

          {/* Archive node requests — RAW count of archive-bucket calls
              (eth_getLogs over historical ranges, debug_*, etc.). These
              also feed the Full counter above proportionally — that's
              by design per the plan's archive_weight. Customers see both
              numbers so they can reason about which bucket is filling up
              fastest. */}
          {archiveLimit ? (
            <div>
              <p className="font-secondary mb-2 text-sm text-white/70">
                Archive node requests
                <span className="ml-2 text-xs text-white/40 font-normal normal-case">
                  (historical state queries)
                </span>
              </p>
              <div className="mb-2 flex items-baseline justify-between gap-2">
                <p className="font-title text-lg font-semibold tracking-tight text-white sm:text-xl">
                  {formatCompactInt(archiveUsed)}{" "}
                  <span className="text-white/40 text-sm font-secondary font-medium">
                    / {formatCompactInt(archiveLimit)}
                  </span>
                </p>
                <span className="font-title text-sm font-semibold" style={{ color: "#a5a0e6" }}>
                  {formatPercent(archivePct)}
                </span>
              </div>
              <AnimatedBar ratio={archiveRatio} color="#7367f0" delay={0.35} />
            </div>
          ) : null}
        </div>
      </div>
    </motion.div>
  );
}
